import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import productService from '../services/productService';
import presentationService from '../services/presentationService';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlinePencil,
  HiOutlineCube,
  HiOutlineX,
  HiOutlineExclamationCircle,
  HiOutlineTrash,
} from 'react-icons/hi';
import './Inventory.css';

const Inventory = () => {
  const { hasRole } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({
    name: '', sku: '', barcode: '', description: '',
    purchase_price: '', wholesale_price: '', retail_price: '',
    stock: '', min_stock: '',
    category: '', unit: 'unidad',
  });
  const [saving, setSaving] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [presentations, setPresentations] = useState([]);
  const [showPresentationForm, setShowPresentationForm] = useState(false);
  const [presForm, setPresForm] = useState({ barcode: '', label: '', units_per_presentation: '', cost_price: '' });
  const [activeTab, setActiveTab] = useState('info'); // 'info' | 'presentations'

  // Scanner: al escanear un código, buscar producto y abrirlo para editar
  const handleBarcodeScan = useCallback(async (code) => {
    setScanFeedback({ code, status: 'searching' });
    try {
      const result = await productService.getByBarcode(code);
      if (result.data) {
        const product = result.data;
        const label = product.presentation_label || 'Unidad';
        const isBox = (product.units_per_presentation || 1) > 1;

        setScanFeedback({ code, status: 'found', product });
        // Abrir el producto para edición
        setEditProduct(product);
        setForm({
          name: product.name || '',
          sku: product.sku || '',
          barcode: product.barcode || '',
          description: product.description || '',
          purchase_price: product.cost_price || '',
          wholesale_price: product.wholesale_price || '',
          retail_price: product.retail_price || '',
          stock: product.stock ?? '',
          min_stock: product.minimum_threshold || '',
          category: product.category || '',
          unit: product.unit || 'unidad',
        });
        setShowModal(true);

        // Cargar presentaciones
        try {
          const presRes = await presentationService.getByProduct(product.id);
          setPresentations(presRes.data || []);
        } catch { setPresentations([]); }

        // Si escanearon una caja, ir directo al tab de presentaciones
        if (isBox) {
          setActiveTab('presentations');
          toast.success(`📦 ${product.name} — ${label}`, { icon: '🔍' });
        } else {
          setActiveTab('info');
          toast.success(`📦 ${product.name}`, { icon: '🔍' });
        }
        setTimeout(() => setScanFeedback(null), 2000);
      }
    } catch {
      setScanFeedback({ code, status: 'not-found' });
      toast.error(`Producto no encontrado: ${code}`);
      // Ofrecer crear nuevo con ese barcode
      setEditProduct(null);
      setForm({
        name: '', sku: '', barcode: code, description: '',
        purchase_price: '', wholesale_price: '', retail_price: '',
        stock: '', min_stock: '',
        category: '', unit: 'unidad',
      });
      setPresentations([]);
      setActiveTab('info');
      setShowModal(true);
      setTimeout(() => setScanFeedback(null), 3000);
    }
  }, []);

  useBarcodeScanner(handleBarcodeScan, { enabled: !showModal });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await productService.getAll();
      setProducts(data.data || data.products || []);
    } catch {
      toast.error('Error cargando productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const filtered = products.filter((p) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.barcode?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q)
    );
  });

  const lowStockCount = products.filter((p) => p.stock !== undefined && p.stock < (p.minimum_threshold || 10)).length;

  const openCreate = () => {
    setEditProduct(null);
    setForm({ name: '', sku: '', barcode: '', description: '', purchase_price: '', wholesale_price: '', retail_price: '', stock: '', min_stock: '', category: '', unit: 'unidad' });
    setPresentations([]);
    setActiveTab('info');
    setShowModal(true);
  };

  const openEdit = async (product) => {
    setEditProduct(product);
    setForm({
      name: product.name || '',
      sku: product.sku || '',
      barcode: product.barcode || '',
      description: product.description || '',
      purchase_price: product.cost_price || '',
      wholesale_price: product.wholesale_price || '',
      retail_price: product.retail_price || '',
      stock: product.stock ?? '',
      min_stock: product.minimum_threshold || '',
      category: product.category || '',
      unit: product.unit || 'unidad',
    });
    setActiveTab('info');
    setShowModal(true);
    // Cargar presentaciones
    try {
      const res = await presentationService.getByProduct(product.id);
      setPresentations(res.data || []);
    } catch {
      setPresentations([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        sku: form.sku || undefined,
        barcode: form.barcode || undefined,
        description: form.description || undefined,
        unit_price: form.retail_price ? Number(form.retail_price) : undefined,
        cost_price: form.purchase_price ? Number(form.purchase_price) : undefined,
        wholesale_price: form.wholesale_price ? Number(form.wholesale_price) : undefined,
        retail_price: form.retail_price ? Number(form.retail_price) : undefined,
        stock: form.stock !== '' ? Number(form.stock) : undefined,
        minimum_threshold: form.min_stock !== '' ? Number(form.min_stock) : undefined,
        unit: form.unit || 'unidad',
      };
      if (editProduct) {
        await productService.update(editProduct.id, payload);
        toast.success('Producto actualizado');
      } else {
        await productService.create(payload);
        toast.success('Producto creado');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Error guardando producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"?\n\nEsta acción desactivará el producto del sistema.`)) return;
    try {
      await productService.delete(product.id);
      toast.success(`Producto "${product.name}" eliminado`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error eliminando producto');
    }
  };

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div>
          <h1>Inventario</h1>
          <p>{products.length} productos · {lowStockCount > 0 && <span className="low-stock-warn"><HiOutlineExclamationCircle /> {lowStockCount} con stock bajo</span>}</p>
        </div>
        {hasRole('admin', 'manager') && (
          <button className="btn-primary" onClick={openCreate}>
            <HiOutlinePlus /> Nuevo Producto
          </button>
        )}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar producto, SKU, código de barras..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className={`scanner-indicator ${scanFeedback ? 'active' : ''}`}>
          <span className="scanner-dot" />
          <span>{scanFeedback ? `Escaneado: ${scanFeedback.code}` : 'Escáner listo'}</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loader"></div><p>Cargando productos...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state-card">
          <HiOutlineCube className="empty-icon" />
          <p>No se encontraron productos</p>
        </div>
      ) : (
        <div className="products-grid">
          {filtered.map((product) => {
            const isLow = product.stock !== undefined && product.stock < (product.minimum_threshold || 10);
            return (
              <div key={product.id} className={`product-card ${isLow ? 'low-stock' : ''}`}>
                <div className="product-header">
                  <div className="product-icon">
                    <HiOutlineCube />
                  </div>
                  <div className="product-meta">
                    <h4>{product.name}</h4>
                    {product.sku && <span className="product-sku">{product.sku}</span>}
                    {product.barcode && <span className="product-barcode">{product.barcode}</span>}
                  </div>
                  {hasRole('admin', 'manager') && (
                    <button className="btn-edit" onClick={() => openEdit(product)}>
                      <HiOutlinePencil />
                    </button>
                  )}
                  {hasRole('admin') && (
                    <button className="btn-delete-card" onClick={() => handleDeleteProduct(product)} title="Eliminar producto">
                      <HiOutlineTrash />
                    </button>
                  )}
                </div>

                <div className="product-details">
                  {product.category && (
                    <span className="product-category">{product.category}</span>
                  )}
                  <div className="product-prices">
                    <div className="price-item">
                      <span className="price-label">Costo</span>
                      <span className="price-value">RD$ {Number(product.cost_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Mayorista</span>
                      <span className="price-value">RD$ {Number(product.wholesale_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Minorista</span>
                      <span className="price-value sale">RD$ {Number(product.retail_price || product.unit_price || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <div className={`stock-info ${isLow ? 'low' : ''}`}>
                    <span className="stock-label">Stock</span>
                    <span className="stock-value">{product.stock ?? 'N/A'} {product.unit || 'und'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editProduct ? 'Editar Producto' : 'Nuevo Producto'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><HiOutlineX /></button>
            </div>

            {/* Tabs solo si estamos editando */}
            {editProduct && (
              <div className="modal-tabs">
                <button
                  className={`modal-tab ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  Información
                </button>
                <button
                  className={`modal-tab ${activeTab === 'presentations' ? 'active' : ''}`}
                  onClick={() => setActiveTab('presentations')}
                >
                  📦 Presentaciones ({presentations.length})
                </button>
              </div>
            )}

            {/* Tab: Info del producto */}
            {activeTab === 'info' && (
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>SKU</label>
                    <input type="text" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Código de Barras</label>
                    <input type="text" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                </div>
                <div className="form-grid-3">
                  <div className="form-group">
                    <label>Precio Compra</label>
                    <input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Venta Mayorista</label>
                    <input type="number" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Venta Minorista</label>
                    <input type="number" step="0.01" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} />
                  </div>
                </div>
                <div className="form-grid-3">
                  <div className="form-group">
                    <label>Stock</label>
                    <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Stock Mín.</label>
                    <input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Unidad</label>
                    <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                      <option value="unidad">Unidad</option>
                      <option value="caja">Caja</option>
                      <option value="paquete">Paquete</option>
                      <option value="libra">Libra</option>
                      <option value="kg">Kg</option>
                      <option value="litro">Litro</option>
                      <option value="galón">Galón</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Categoría</label>
                  <input type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej: Bebidas, Snacks..." />
                </div>
                <button type="submit" className="btn-primary full" disabled={saving}>
                  {saving ? 'Guardando...' : editProduct ? 'Actualizar' : 'Crear Producto'}
                </button>
              </form>
            )}

            {/* Tab: Presentaciones */}
            {activeTab === 'presentations' && editProduct && (
              <div className="presentations-tab">
                <p className="presentations-hint">
                  Cada presentación tiene un código de barras diferente. 
                  Al escanear un código de caja, se multiplica automáticamente la cantidad de unidades.
                </p>

                <div className="presentations-list">
                  {presentations.map((pres) => (
                    <div key={pres.id} className="presentation-item">
                      <div className="presentation-info">
                        <span className="presentation-label">{pres.label}</span>
                        <span className="presentation-barcode">{pres.barcode}</span>
                      </div>
                      <div className="presentation-details">
                        <span className="presentation-units">{pres.units_per_presentation} und</span>
                        {pres.cost_price && (
                          <span className="presentation-cost">
                            RD$ {Number(pres.cost_price).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                      {pres.units_per_presentation > 1 && hasRole('admin', 'manager') && (
                        <button
                          className="btn-delete-sm"
                          onClick={async () => {
                            if (!window.confirm(`¿Eliminar presentación "${pres.label}"?`)) return;
                            try {
                              await presentationService.remove(pres.id);
                              setPresentations((prev) => prev.filter((p) => p.id !== pres.id));
                              toast.success('Presentación eliminada');
                            } catch {
                              toast.error('Error eliminando presentación');
                            }
                          }}
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  ))}
                  {presentations.length === 0 && (
                    <p className="no-presentations">No hay presentaciones configuradas</p>
                  )}
                </div>

                {/* Form para agregar presentación */}
                {hasRole('admin', 'manager') && (
                  <>
                    {!showPresentationForm ? (
                      <button
                        className="btn-secondary full"
                        onClick={() => {
                          setShowPresentationForm(true);
                          setPresForm({ barcode: '', label: '', units_per_presentation: '', cost_price: '' });
                        }}
                      >
                        <HiOutlinePlus /> Agregar Presentación (Caja, Fardo, etc.)
                      </button>
                    ) : (
                      <div className="presentation-form">
                        <h4>Nueva Presentación</h4>
                        <div className="form-grid-2">
                          <div className="form-group">
                            <label>Etiqueta *</label>
                            <input
                              type="text"
                              placeholder="Ej: Caja x24, Fardo x6"
                              value={presForm.label}
                              onChange={(e) => setPresForm({ ...presForm, label: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Código de Barras *</label>
                            <input
                              type="text"
                              placeholder="Escanear o escribir"
                              value={presForm.barcode}
                              onChange={(e) => setPresForm({ ...presForm, barcode: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="form-grid-2">
                          <div className="form-group">
                            <label>Unidades por Presentación *</label>
                            <input
                              type="number"
                              min="2"
                              placeholder="Ej: 24"
                              value={presForm.units_per_presentation}
                              onChange={(e) => setPresForm({ ...presForm, units_per_presentation: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Costo de Compra (opcional)</label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Costo de la presentación"
                              value={presForm.cost_price}
                              onChange={(e) => setPresForm({ ...presForm, cost_price: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            className="btn-primary"
                            disabled={saving}
                            onClick={async () => {
                              if (!presForm.barcode || !presForm.label || !presForm.units_per_presentation) {
                                toast.error('Completa los campos requeridos');
                                return;
                              }
                              setSaving(true);
                              try {
                                const res = await presentationService.create(editProduct.id, {
                                  barcode: presForm.barcode,
                                  label: presForm.label,
                                  units_per_presentation: Number(presForm.units_per_presentation),
                                  cost_price: presForm.cost_price ? Number(presForm.cost_price) : undefined,
                                });
                                setPresentations((prev) => [...prev, res.data]);
                                setShowPresentationForm(false);
                                toast.success(`Presentación "${presForm.label}" creada`);
                              } catch (err) {
                                toast.error(err.response?.data?.error || 'Error creando presentación');
                              } finally {
                                setSaving(false);
                              }
                            }}
                          >
                            {saving ? 'Guardando...' : 'Guardar Presentación'}
                          </button>
                          <button className="btn-ghost" onClick={() => setShowPresentationForm(false)}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
